import React, { useMemo } from 'react';
import { Shape, Line, Circle, Group as KonvaGroup, Group } from 'react-konva';
import DrawingText from './DrawingText';
import VertexHandle from './VertexHandle';
import SegmentMeasurementOverlay from './SegmentMeasurementOverlay';
import { resolveAllParams } from '../../ShapeList/paramUtils';
import {
  getDisplayMeasurement,
  enhanceShapeWithMeasurements,
  calculatePolygonCentroid,
} from '../utils/drawingUtils';
import { calculateTotalAreaWithHoles } from '../utils/booleanOperations';
import {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_MEASUREMENT,
  DISPLAY_KEY_AREA,
} from './ShapeRenderer';

const SELECTED_GLOW = {
  shadowColor: '#3B82F6',
  shadowBlur: 18,
  shadowOpacity: 0.7,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
};

const HOLE_VERTEX_COLOR = null;

const AreaShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale,
  activeStrokeColor,
  shapeStroke,
  calculatedStrokeWidth,
  selectionStrokeWidth,
  vertexMarkerRadius,
  selectedStrokeHighlightColor,
  hoverColor,
  onVertexClick,
  shouldShowText,
  displayKeys,
  textFontSize,
  shapeScaleProps,
  dpi,
  imageSize,
  displayUnitSystem,
  isDrawingPreview,
  vertexEditInfo,
  showOverlay = true,
  hoveredSegmentInfo,
}) => {
  const scaledShape = shape;

  const isTextTooSmall = (text, metricLength, factor = 1.1) => {
    if (isSelected) return false;
    const estimatedTextWidth = String(text).length * textFontSize * 0.6;
    return metricLength < estimatedTextWidth * factor;
  };

  const enhancedForDisplay = useMemo(() => {
    return enhanceShapeWithMeasurements(
      scaledShape,
      shapeScaleProps,
      dpi,
      imageSize,
      displayUnitSystem,
    );
  }, [
    shape?.id,
    shape?._measurementCacheKey,
    shape?.points,
    shape?.holes,
    shape?.displayAreaMeasurement?.value,
    shape?.displayTotalMeasurement?.value,
    shape?.segmentDistances?.length,
    shapeScaleProps?.key,
    shapeScaleProps?.ratio,
    dpi,
    imageSize?.width,
    imageSize?.height,
    displayUnitSystem,
  ]);
  const numVertices = enhancedForDisplay.points
    ? enhancedForDisplay.points.length / 2
    : 0;

  const polygonArea = calculateTotalAreaWithHoles(
    shape.points,
    shape.holes,
  );
  const centroid = calculatePolygonCentroid(
    enhancedForDisplay.points,
    enhancedForDisplay.x,
    enhancedForDisplay.y,
  );
  const areaDisplay = enhancedForDisplay.displayAreaMeasurement
    ? `${enhancedForDisplay.displayAreaMeasurement.value} ${enhancedForDisplay.displayAreaMeasurement.unit}`
    : '';

  const perimeterDisplay = enhancedForDisplay.displayTotalMeasurement
    ? `${enhancedForDisplay.displayTotalMeasurement.value} ${enhancedForDisplay.displayTotalMeasurement.unit}`
    : '';

  const calculatedParamsForArea = {
    Area: areaDisplay,
    'Total Area': areaDisplay,
    TotalArea: areaDisplay,
    'Total Length': perimeterDisplay,
    Length: perimeterDisplay,
    Perimeter: perimeterDisplay,
    [DISPLAY_KEY_MEASUREMENT]: perimeterDisplay,
  };
  const allResolvedParamsForArea = resolveAllParams(
    shape.params,
    calculatedParamsForArea,
  );

  const drawingColor = shapeStroke || '#000000';
  const areaFill = drawingColor.startsWith('#')
    ? `${drawingColor}33`
    : `rgba(${drawingColor.match(/\d+/g)?.join(',') || '0,0,0'}, 0.2)`;

  return (
    <KonvaGroup {...commonProps}>
      <Line
        name="area_outline"
        points={scaledShape.points}
        stroke={commonProps.stroke}
        strokeWidth={
          isSelected
            ? selectionStrokeWidth
            : calculatedStrokeWidth * 1.5
        }
        lineCap="round"
        lineJoin="round"
        closed={true}
        fill={areaFill}
        listening={true}
        hitStrokeWidth={Math.max(10, calculatedStrokeWidth + 8) / stageScale}
        perfectDrawEnabled={false}
        onMouseEnter={commonProps.onMouseEnter}
        onMouseLeave={commonProps.onMouseLeave}
        {...(isSelected ? SELECTED_GLOW : {})}
      />
      <Shape
        name="area_fill_shape"
        sceneFunc={(context, shapeNode) => {
          context.beginPath();

          const outerPoints = scaledShape.points;
          if (Array.isArray(outerPoints) && outerPoints.length >= 2) {
            context.moveTo(outerPoints[0], outerPoints[1]);
            for (let i = 2; i < outerPoints.length; i += 2) {
              context.lineTo(outerPoints[i], outerPoints[i + 1]);
            }
            context.closePath();
          }

          if (Array.isArray(scaledShape.holes)) {
            scaledShape.holes.forEach((hole) => {
              if (Array.isArray(hole) && hole.length >= 2) {
                context.moveTo(hole[0], hole[1]);
                for (let i = 2; i < hole.length; i += 2) {
                  context.lineTo(hole[i], hole[i + 1]);
                }
                context.closePath();
              }
            });
          }

          context.fillStrokeShape(shapeNode);
        }}
        fill={areaFill}
        stroke={commonProps.stroke}
        hitStrokeWidth={Math.max(15, commonProps.strokeWidth)}
        strokeWidth={
          isSelected
            ? selectionStrokeWidth
            : calculatedStrokeWidth * 1.5
        }
        fillRule="evenodd"
        listening={true}
        onMouseEnter={commonProps.onMouseEnter}
        onMouseLeave={commonProps.onMouseLeave}
      />

      {scaledShape.holes &&
        scaledShape.holes.map((hole, holeIndex) => (
          <Line
            key={`area-hole-${scaledShape.id}-${holeIndex}`}
            points={hole}
            stroke={commonProps.stroke}
            strokeWidth={commonProps.strokeWidth}
            fill={'rgba(255, 255, 255, 0.5)'}
            lineCap="round"
            lineJoin="round"
            closed={true}
            listening={true}
            onMouseEnter={commonProps.onMouseEnter}
            onMouseLeave={commonProps.onMouseLeave}
          />
        ))}

      {scaledShape.points &&
        (scaledShape.points.length / 2) >= 2 &&
        enhancedForDisplay.segmentDistances &&
        enhancedForDisplay.segmentDistances.map((dist, index) => {
          const rawNumVertices = scaledShape.points.length / 2;
          const p1x = scaledShape.points[index * 2];
          const p1y = scaledShape.points[index * 2 + 1];

          const p2Index = (index + 1) % rawNumVertices;
          const p2x = scaledShape.points[p2Index * 2];
          const p2y = scaledShape.points[p2Index * 2 + 1];

          if (typeof p2x === 'undefined' || typeof p2y === 'undefined')
            return null;

          const midX = (p1x + p2x) / 2;
          const midY = (p1y + p2y) / 2;
          const angleRad = Math.atan2(p2y - p1y, p2x - p1x);
          let angleDeg = angleRad * (180 / Math.PI);

          if (angleDeg > 90) angleDeg -= 180;
          else if (angleDeg < -90) angleDeg += 180;

          const measurement = getDisplayMeasurement(
            dist,
            shapeScaleProps,
            dpi,
            imageSize,
            displayUnitSystem,
          );
          const segmentDisplay = `${measurement.value} ${measurement.unit}`;

          const isSegmentBeingDrawn = isDrawingPreview && (index === rawNumVertices - 2);
          const isSegmentBeingEdited = isSelected && vertexEditInfo && vertexEditInfo.shapeId === shape.id && !vertexEditInfo.isHole && (index === (vertexEditInfo.vertexIndex - 1 + rawNumVertices) % rawNumVertices || index === vertexEditInfo.vertexIndex);
          const showSegmentOverlay = isSegmentBeingDrawn || isSegmentBeingEdited;

          const showSegmentText = !showSegmentOverlay && shouldShowText && displayKeys.includes(DISPLAY_KEY_SEGMENT_LENGTH) && !isTextTooSmall(segmentDisplay, dist);

          if (!showSegmentOverlay && !showSegmentText) return null;

          return (
            <Group key={`area-segment-group-${scaledShape.id}-${index}`}>
              {showSegmentOverlay && (
                <SegmentMeasurementOverlay
                  x1={p1x}
                  y1={p1y}
                  x2={p2x}
                  y2={p2y}
                  activeStrokeColor={activeStrokeColor}
                  stageScale={stageScale}
                  textFontSize={textFontSize}
                  shapeScaleProps={shapeScaleProps}
                  dpi={dpi}
                  imageSize={imageSize}
                  displayUnitSystem={displayUnitSystem}
                  showOverlay={showOverlay}
                />
              )}
              {showSegmentText && (
                <DrawingText
                  key={`area-seg-${scaledShape.id}-${index}`}
                  x={midX}
                  y={midY}
                  text={segmentDisplay}
                  fontSize={textFontSize}
                  fill={activeStrokeColor}
                  rotation={angleDeg}
                  offsetY={-textFontSize * 0.8}
                  offsetX={0}
                  stageScale={stageScale}
                  useBadge={true}
                  badgeStroke={activeStrokeColor}
                />
              )}
            </Group>
          );
        })}

      {shouldShowText && polygonArea > 0 &&
        displayKeys.includes(DISPLAY_KEY_AREA) &&
        areaDisplay && !isTextTooSmall(areaDisplay, Math.sqrt(polygonArea)) && (
          <DrawingText
            key={`area-display-${scaledShape.id}`}
            x={centroid.x - scaledShape.x}
            y={centroid.y - scaledShape.y}
            text={areaDisplay}
            fontSize={textFontSize}
            fill={activeStrokeColor}
            align="center"
            verticalAlign="middle"
            offsetX={0}
            offsetY={0}
            stageScale={stageScale}
            useBadge={true}
            badgeStroke={activeStrokeColor}
          />
        )}

      {shouldShowText && displayKeys.length > 0 &&
        allResolvedParamsForArea &&
        displayKeys
          .filter(
            (dk) =>
              dk !== DISPLAY_KEY_AREA &&
              dk !== DISPLAY_KEY_SEGMENT_LENGTH,
          )
          .map((dk, dkIdx) => {
            const val = allResolvedParamsForArea[dk];
            if (!val) return null;
            if (isTextTooSmall(val, Math.sqrt(polygonArea), dk === DISPLAY_KEY_MEASUREMENT ? 1.1 : 0.8)) return null;
            return (
              <DrawingText
                key={`area-dk-${scaledShape.id}-${dkIdx}`}
                x={centroid.x - scaledShape.x}
                y={centroid.y - scaledShape.y}
                text={`${String(val)}`}
                fontSize={textFontSize}
                fill={activeStrokeColor}
                align="center"
                verticalAlign="middle"
                offsetX={0}
                offsetY={-textFontSize * (1.2 + dkIdx)}
                stageScale={stageScale}
                useBadge={true}
                badgeStroke={activeStrokeColor}
              />
            );
          })}

      {hoveredSegmentInfo &&
        hoveredSegmentInfo.shapeId === shape.id &&
        !vertexEditInfo && (
          <Group listening={false}>
            <Circle
              x={hoveredSegmentInfo.x - shape.x}
              y={hoveredSegmentInfo.y - shape.y}
              radius={vertexMarkerRadius * 1.4}
              fill="#3B82F6"
              stroke="#FFFFFF"
              strokeWidth={Math.max(0.5, 1 / stageScale)}
              perfectDrawEnabled={false}
            />
            <Circle
              x={hoveredSegmentInfo.x - shape.x}
              y={hoveredSegmentInfo.y - shape.y}
              radius={vertexMarkerRadius * 0.5}
              fill="#FFFFFF"
              perfectDrawEnabled={false}
            />
          </Group>
        )}

      {isSelected &&
        scaledShape.points &&
        scaledShape.points.map((_, index) => {
          if (index % 2 === 0) {
            const vertexX = scaledShape.points[index];
            const vertexY = scaledShape.points[index + 1];
            return (
              <VertexHandle
                key={`vtx-area-${scaledShape.id}-${index / 2}`}
                x={vertexX}
                y={vertexY}
                radius={vertexMarkerRadius}
                color={selectedStrokeHighlightColor}
                hoverColor={hoverColor}
                onMouseDown={(e) =>
                  onVertexClick(
                    e,
                    shape.id,
                    index / 2,
                    false,
                    0,
                  )
                }
                onTouchStart={(e) =>
                  onVertexClick(
                    e,
                    shape.id,
                    index / 2,
                    false,
                    0,
                  )
                }
                stageScale={stageScale}
              />
            );
          }
          return null;
        })}

      {isSelected &&
        shape.holes &&
        shape.holes.map((hole, holeIndex) =>
          hole.map((_, index) => {
            if (index % 2 === 0) {
              const vertexX = hole[index];
              const vertexY = hole[index + 1];
              const holeVertexRadius = vertexMarkerRadius * 0.8;
              const holeVertexFill =
                HOLE_VERTEX_COLOR || shapeStroke;
              return (
                <VertexHandle
                  key={`vtx-area-hole-${shape.id}-${holeIndex}-${index / 2}`}
                  x={vertexX}
                  y={vertexY}
                  radius={holeVertexRadius}
                  color={holeVertexFill}
                  hoverColor={hoverColor}
                  onMouseDown={(e) =>
                    onVertexClick(
                      e,
                      shape.id,
                      index / 2,
                      true,
                      holeIndex,
                    )
                  }
                  onTouchStart={(e) =>
                    onVertexClick(
                      e,
                      shape.id,
                      index / 2,
                      true,
                      holeIndex,
                    )
                  }
                  stageScale={stageScale}
                />
              );
            }
            return null;
          }),
        )}
    </KonvaGroup>
  );
};

export default AreaShape;
